import { IDodaButton } from '@nocode/types';
import { get } from 'lodash';
import React, { useState } from 'react';
import { Alert, Text, TouchableHighlight, View } from 'react-native';
import { useDodaCampusLoginAction } from '@common/hooks/useDodaCampusLoginAction';
import { getActions, getValueBinding } from '../../shared';
import createStyles from './style';

/**
 * Normalises a data-bound value into the string the login endpoint expects.
 * getValueBinding is typed `Record<string, boolean | number | string>`, so the
 * bound value is whatever element the builder wired up: a number-typed Input is
 * a legitimate source for an all-digit password, but a boolean never is, and
 * must not be coerced into the string "true".
 */
const asCredential = (value: boolean | number | string | undefined): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
};

const DodaLogin: React.FC<IDodaButton> = (props) => {
  const {
    attributes,
    onPress,
    id,
    data,
    style,
    screenUuid,
    dependencies,
    dodaCampusLogin,
  } = props;
  const styles = createStyles({ ...attributes, ...style });

  // The login call is obtained here rather than relied on as an injected prop.
  // LibraryComponent injects it via useCustomProp, but the published share app
  // renders this element without it, so `dodaCampusLogin` arrived undefined and
  // calling it threw -- which the catch below reported as an ordinary login
  // failure, with no request ever sent. The injected prop still wins when it is
  // present, so nothing changes on the surfaces where injection works.
  const { dodaCampusLogin: ownDodaCampusLogin } = useDodaCampusLoginAction();
  const login = dodaCampusLogin ?? ownDodaCampusLogin;

  const bindingValue = getValueBinding(id, data, props);
  const isConfigured =
    !!attributes?.dc_user_id?.fid && !!attributes?.email?.fid;
  const appId = get(dependencies, 'appInfor.appId');

  const [loading, setLoading] = useState(false);

  const handlePress = async () => {
    // The "Button" slot fires on every tap, before the login is attempted --
    // successActions/failedActions are what report the outcome. Dispatched
    // the same way Button and ActionButton dispatch their own 'action' prop.
    onPress && onPress(getActions(props, 'action'), {});

    if (!isConfigured) {
      Alert.alert(attributes?.textError?.en || 'Component setup incomplete.');
      onPress && onPress(getActions(props, 'failedActions'), {});
      return;
    }

    const email = asCredential(get(bindingValue, 'login_email'));
    const password = asCredential(get(bindingValue, 'login_password'));
    if (!email || !password) {
      Alert.alert('Please enter your email and password.');
      return;
    }
    if (loading) return;

    setLoading(true);
    try {
      await login({
        appId,
        screenUuid: screenUuid || '',
        objectId: id,
        email,
        password,
      });
      onPress && onPress(getActions(props, 'successActions'), {});
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        'doda Campus login failed. Please try again.';
      Alert.alert(message);
      onPress && onPress(getActions(props, 'failedActions'), {});
    } finally {
      setLoading(false);
    }
  };

  if (!isConfigured) {
    return (
      <TouchableHighlight onPress={handlePress}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{attributes?.textError?.en}</Text>
        </View>
      </TouchableHighlight>
    );
  }

  return (
    <TouchableHighlight onPress={handlePress} disabled={loading}>
      <View style={styles.container}>
        <Text style={styles.text}>{get(bindingValue, 'text')}</Text>
      </View>
    </TouchableHighlight>
  );
};

export default DodaLogin;
